/**
 * Field Editability Engine
 *
 * Determines which fields are editable for a given DocType by applying
 * an 8-layer decision tree against DocType metadata, then applying
 * per-DocType whitelist/blacklist overrides.
 *
 * Used by the standard table inline editing (Plan 161) and can replace
 * hardcoded advancedEditableFields in list pages.
 */

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { erp } from '@/lib/erp';
import type { DocTypeSchema } from '@/lib/erp/meta';
import { READONLY_SYSTEM_FIELDS } from '@/components/data-grid/types';
import {
  FIELD_EDITABILITY_OVERRIDES,
  type FieldEditabilityOverride,
} from '@/features/common/config/fieldEditabilityOverrides';

/**
 * Fieldtypes that are excluded from inline editing.
 * These are either layout elements (no data), child tables (separate paradigm),
 * or types that need custom UI (file upload).
 */
const EXCLUDED_FIELDTYPES = new Set([
  // Layout — no data
  'Section Break',
  'Column Break',
  'Tab Break',
  'Heading',
  'HTML',
  'Button',
  // Child tables — separate editing paradigm
  'Table',
  'Table MultiSelect',
  // Unsupported — need file upload UI, not inline text edit
  'Attach',
  'Attach Image',
  // Read Only fieldtype (distinct from read_only=1 property)
  'Read Only',
]);

/**
 * Pure function: computes the set of editable fieldnames from DocType schema.
 *
 * Decision tree (in order):
 * 1. DocType write permission required
 * 2. System fields (name, creation, etc.) → never editable
 * 3. Hidden fields → skip
 * 4. Excluded fieldtypes (layout, tables, attachments) → skip
 * 5. read_only = 1 → not editable
 * 6. set_only_once = 1 → not editable (conservative: treats all existing docs as having values)
 * 7. permlevel > 0 → not editable (safe default; full perm check is P1)
 * 8. fetch_from set → not editable (editing desynchronizes from source)
 *
 * After the decision tree, per-DocType overrides (blacklist/whitelist) are applied.
 */
export function computeEditableFields(
  schema: DocTypeSchema,
  overrides?: FieldEditabilityOverride,
): Set<string> {
  // 1. DocType write permission required
  if (!schema.permissions?.write) return new Set();

  const editable = new Set<string>();

  for (const field of schema.fields) {
    const fn = field.fieldname;

    // 2. System fields — never editable
    if (READONLY_SYSTEM_FIELDS.has(fn)) continue;

    // 3. Hidden fields — not shown, skip
    if (field.hidden) continue;

    // 4. Excluded fieldtypes
    if (EXCLUDED_FIELDTYPES.has(field.fieldtype)) continue;

    // 5. read_only = 1
    if (field.read_only) continue;

    // 6. set_only_once = 1 (for existing docs, treat as read-only)
    if (field.set_only_once) continue;

    // 7. permlevel > 0 (requires elevated permissions)
    if ((field.permlevel ?? 0) > 0) continue;

    // 8. fetch_from — auto-populated, editing desynchronizes from source
    if (field.fetch_from) continue;

    // Passed all checks — field is editable
    editable.add(fn);
  }

  // Apply per-DocType overrides
  if (overrides?.blacklist) {
    for (const fn of overrides.blacklist) editable.delete(fn);
  }
  if (overrides?.whitelist) {
    for (const fn of overrides.whitelist) editable.add(fn);
  }

  return editable;
}

/**
 * Hook: returns the set of editable fieldnames for a DocType.
 *
 * Uses React Query to fetch and cache DocType meta (shares cache key
 * with useChildMeta: ['meta', doctype]).
 *
 * @param doctype - DocType name (e.g. "Item", "Customer", "Warehouse")
 * @returns editableFields Set, isFieldEditable helper, and loading state
 */
export function useFieldEditability(doctype: string) {
  const { data: schema, isLoading } = useQuery<DocTypeSchema>({
    queryKey: ['meta', doctype],
    queryFn: () => erp.meta.getDocTypeSchema(doctype),
    enabled: !!doctype,
    staleTime: 5 * 60 * 1000, // 5 minutes — meta doesn't change during a session
  });

  const editableFields = useMemo(() => {
    if (!schema) return new Set<string>();
    const overrides = FIELD_EDITABILITY_OVERRIDES[doctype];
    return computeEditableFields(schema, overrides);
  }, [schema, doctype]);

  const isFieldEditable = useCallback(
    (fieldname: string) => editableFields.has(fieldname),
    [editableFields],
  );

  return { editableFields, isFieldEditable, isLoading };
}
